import React, { createContext, useContext, useEffect, useState } from "react";
import {
  getReferals,
  getYIdByTgId,
  getInviteShareText,
  getUserStatus,
  getEatsLinkUnlocked,
} from "@/api/base";
import { ReferalsResponse, InviteText, UserStatus } from "@/types/referals";
import { useSearchParams } from "react-router-dom";
import { useUserInfoStore } from "@/store/userInfoStore";
import { DEFAULT_SORT_PARAM } from "@/store/sortTypeStore";
import { useSentry } from "@/utils/hooks/useSentry";

interface ReferalsContextValue {
  inviteText: InviteText | null;
  referalsLoading: boolean;
  userStatus: UserStatus | null;
  eatsLinkUnlocked: boolean;

  activeReferals: ReferalsResponse;
  missedReferals: ReferalsResponse;
  unregisteredReferals: ReferalsResponse;
  completedReferals: ReferalsResponse;
  loadActiveReferals: (offset?: number, sort?: string) => Promise<void>;
  loadUnregisteredReferals: (offset?: number) => Promise<void>;
  loadMissedReferals: (offset?: number) => Promise<void>;
  loadCompletedReferals: (offset?: number) => Promise<void>;
}

const ReferalsContext = createContext<ReferalsContextValue | undefined>(
  undefined
);

export const ReferalsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { setUser } = useSentry();
  const [referalsLoading, setReferalsLoading] = useState<boolean>(true);
  const [activeReferals, setActiveReferals] = useState<ReferalsResponse>(
    {} as ReferalsResponse
  );
  const [
    unregisteredReferals,
    setUnregisteredReferals,
  ] = useState<ReferalsResponse>({} as ReferalsResponse);
  const [missedReferals, setMissedReferals] = useState<ReferalsResponse>(
    {} as ReferalsResponse
  );
  const [completedReferals, setCompletedReferals] = useState<ReferalsResponse>(
    {} as ReferalsResponse
  );

  const [searchParams] = useSearchParams();
  const yandexId = useUserInfoStore((s) => s.yandexId);
  const setYandexId = useUserInfoStore((s) => s.setYandexId);

  const serviceType = useUserInfoStore((s) => s.serviceType);
  const [inviteText, setInviteText] = useState<InviteText | null>(null);

  const [userStatus, setUserStatus] = useState<UserStatus | null>(null);
  const [eatsLinkUnlocked, setEatsLinkUnlocked] = useState<boolean>(false);

  const loadActiveReferals = async (
    offset: number = 0,
    sort: string = DEFAULT_SORT_PARAM
  ) => {
    try {
      const res = await getReferals(yandexId, "active", offset, sort);
      setActiveReferals(res);
    } catch (e) {
      console.log(e);
    }
  };

  const loadMissedReferals = async (offset: number = 0) => {
    try {
      const res = await getReferals(
        yandexId,
        "missed",
        offset,
        DEFAULT_SORT_PARAM
      );
      setMissedReferals(res);
    } catch (e) {
      console.log(e);
    }
  };

  const loadUnregisteredReferals = async (offset: number = 0) => {
    try {
      const res = await getReferals(
        yandexId,
        "unregistered",
        offset,
        DEFAULT_SORT_PARAM
      );
      setUnregisteredReferals(res);
    } catch (e) {
      console.log(e);
    }
  };

  const loadCompletedReferals = async (offset: number = 0) => {
    try {
      const res = await getReferals(
        yandexId,
        "completed",
        offset,
        DEFAULT_SORT_PARAM
      );
      setCompletedReferals(res);
    } catch (e) {
      console.log(e);
    }
  };

  const loadInviteText = async () => {
    try {
      const res = await getInviteShareText();
      setInviteText(res);
    } catch (e) {
      console.log(e);
    }
  };

  const loadUserStatus = async () => {
    if (!serviceType) return;

    try {
      const res = await getUserStatus(yandexId, serviceType);
      setUserStatus(res);
    } catch (e) {
      console.log(e);
    }
  };

  const loadEatsLinkStatus = async () => {
    if (serviceType !== "delivery" && serviceType !== "taxi") {
      setEatsLinkUnlocked(false);
      return;
    }

    try {
      setEatsLinkUnlocked(await getEatsLinkUnlocked(yandexId, serviceType));
    } catch (e) {
      console.log(e);
      setEatsLinkUnlocked(false);
    }
  };

  const loadData = async () => {
    try {
      setReferalsLoading(true);

      loadUserStatus();
      loadEatsLinkStatus();
      loadActiveReferals();
      loadMissedReferals();
      loadUnregisteredReferals();
      loadCompletedReferals();
    } catch (e) {
      console.log(e);
    } finally {
      setReferalsLoading(false);
    }
  };

  useEffect(() => {
    const paramYId = searchParams.get("yandex_id");
    const paramTgId = searchParams.get("telegram_id");

    if (paramYId) {
      setYandexId(paramYId);
      setUser({ yandexId: paramYId });
      return;
    }

    if (paramTgId)
      getYIdByTgId(paramTgId)
        .then((res) => {
          const yandexId = res?.yandex_id || "";
          setYandexId(yandexId);
          setUser({ yandexId, telegramId: paramTgId });
        })
        .catch((e) => {
          console.error(e);
          setYandexId("");
        });
  }, [searchParams]);

  useEffect(() => {
    if (!yandexId || !serviceType) {
      setReferalsLoading(false);
      return;
    }

    loadData();
  }, [yandexId, serviceType]);

  useEffect(() => {
    loadInviteText();
  }, []);

  return (
    <ReferalsContext.Provider
      value={{
        inviteText,
        referalsLoading,
        userStatus,
        eatsLinkUnlocked,

        activeReferals,
        missedReferals,
        unregisteredReferals,
        completedReferals,
        loadActiveReferals,
        loadUnregisteredReferals,
        loadMissedReferals,
        loadCompletedReferals,
      }}
    >
      {children}
    </ReferalsContext.Provider>
  );
};

export const useReferalsDataContext = (): ReferalsContextValue => {
  const ctx = useContext(ReferalsContext);
  if (!ctx) {
    throw new Error(
      "useReferalsDataContext must be used within ReferalsProvider"
    );
  }
  return ctx;
};
